import { FormControl, FormControlProps, FormHelperText } from '@mui/material';
import { useId } from 'react';
import { Controller, useFormContext, ValidateResult } from 'react-hook-form';
import { DateRange, DateRangeValue } from 'src/components/DateRange/date-range';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';

export type FormDateRangeProps = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'name'
> & {
  required?: boolean | string;
  name: string;
  label?: string;
  value?: DateRangeValue;
  validate?: (value: DateRangeValue) => ValidateResult;
} & CommonFormFieldProps<'daterange', DateRangeValue | null | undefined>;

// A range is a group of four pickers, so there is no single box for a floating label to sit in:
// its label always sits above, whatever `labelAbove` says.
export const FormDateRange = (props: FormDateRangeProps) => {
  const {
    name,
    label,
    required,
    value,
    defaultValue,
    validate,
    labelAbove: _labelAbove,
    size,
    ...rest
  } = props;
  const { control, formState } = useFormContext();
  const error = formState.errors[name];
  const labelId = useId();
  const groupProps = label ? { role: 'group', 'aria-labelledby': labelId } : {};

  return (
    <FormControl size={size} {...rest} {...groupProps} error={!!error}>
      <Controller
        name={name}
        control={control}
        rules={{
          validate,
        }}
        render={({ field }) => (
          <>
            {label ? <FormFieldLabel id={labelId}>{label}</FormFieldLabel> : null}
            <DateRange {...field} size={size} />
            {error?.message && typeof error?.message === 'string' ? (
              <FormHelperText error={!!error}>{error.message}</FormHelperText>
            ) : null}
          </>
        )}
      />
    </FormControl>
  );
};
